import {
  EventTrackingAction,
  TrackingOptions, // eslint-disable-next-line import/no-unresolved
} from '@opendoor/observability/slim';

/**
 * API Reference: https://developers.google.com/identity/gsi/web/reference/js-reference#CredentialResponse
 */
export interface CredentialResponse {
  credential: string;
}

/**
 * Guide: https://developers.google.com/identity/gsi/web/guides/receive-notifications-prompt-ui-status
 * API Reference: https://developers.google.com/identity/gsi/web/reference/js-reference#PromptMomentNotification
 */
interface PromptMomentNotification {
  // possible values: display, dismissed, skipped
  getMomentType(): string | undefined;
  isDismissedMoment(): boolean;
  // possible values: credential_returned, cancel_called, flow_restarted
  getDismissedReason(): string | undefined;
  isSkippedMoment(): boolean;
}

/**
 * Guide: https://developers.google.com/identity/gsi/web/guides/use-one-tap-js-api
 * API Reference: https://developers.google.com/identity/gsi/web/reference/js-reference
 *
 * use_fedcm_for_prompt: Allow the browser to control user sign-in prompts and mediate the sign-in flow between your website and Google.
 * https://developers.google.com/identity/gsi/web/reference/js-reference#use_fedcm_for_prompt
 */
interface GoogleOneTap {
  accounts: {
    id: {
      initialize(options: {
        client_id: string;
        callback: (response: CredentialResponse) => void;
        use_fedcm_for_prompt: boolean;
      }): void;
      prompt(callback?: (notification: PromptMomentNotification) => void): void;
    };
  };
}

let googleOneTapPromise: Promise<GoogleOneTap | undefined> | undefined;

function loadGoogleOneTap(): Promise<GoogleOneTap | undefined> {
  if (!googleOneTapPromise) {
    googleOneTapPromise = new Promise((resolve) => {
      if (typeof window === 'undefined') {
        return undefined;
      }

      // eslint-disable-next-line no-var
      var script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.defer = true;
      script.src =
        ('https:' === document.location.protocol ? 'https://' : 'http://') +
        'accounts.google.com/gsi/client';
      script.onload = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const google = (window as any).google;
        if (google?.accounts?.id?.initialize && google?.accounts.id?.prompt) {
          resolve(google as GoogleOneTap);
        } else {
          resolve(undefined);
        }
      };
      script.onerror = () => resolve(undefined);

      // Insert our script next to the first script element.
      // eslint-disable-next-line no-var
      var first = document.getElementsByTagName('script')[0];
      // @ts-expect-error fix later
      first.parentNode.insertBefore(script, first);
    });
  }
  return googleOneTapPromise;
}

export interface PromptGoogleOneTapOptions<AppCategory = undefined, AppActions = undefined> {
  analyticsName: string;
  clientId: string;

  /** This occurs when Google successfully retrieves a credential. Pass the credential to our servers. */
  onSuccess: (response: CredentialResponse) => void;

  /**
   * This occurs after the prompt() method is called.
   * The reason may be undefined when not displayed as we can not get the exact reason due to privacy restrictions.
   * Users now have the ability to disable one-tap from displaying from the browser settings, etc.
   * See also: https://developers.google.com/identity/gsi/web/guides/receive-notifications-prompt-ui-status
   */
  onPrompted?: (isDisplayed: boolean, reason: string | undefined) => void;
  /**
   * This occurs when Google successfully retrieves a credential, or a user wants to stop the credential retrieval flow. For example, when the user begins to input their username and password into the login dialog, you can call the google.accounts.id.cancel() method to close the One Tap prompt and trigger a dismissed moment.
   * See also: https://developers.google.com/identity/gsi/web/guides/receive-notifications-prompt-ui-status
   */
  onDismissed?: (reason: string | undefined) => void;
  /**
   * This occurs when the One Tap prompt is closed by an auto cancel, a manual cancel, or when Google fails to issue a credential, such as when the selected session is signed out of Google.
   * See also: https://developers.google.com/identity/gsi/web/guides/receive-notifications-prompt-ui-status
   */
  onSkipped?: () => void;
  /**
   * Analytics tracking function from client
   */
  trackEvent: (
    action: EventTrackingAction<AppActions>,
    label?: string,
    data?: { [k: string]: unknown } & { search?: string },
    options?: TrackingOptions<AppCategory>,
  ) => void;
  /**
   * Error tracking function from client
   */
  trackError: (
    label: string,
    error?: Error,
    data?: { [k: string]: unknown } & { search?: string },
    options?: TrackingOptions<AppCategory>,
  ) => void;
}

export async function promptGoogleOneTap<AppCategory = undefined, AppActions = undefined>({
  analyticsName,
  clientId,
  onSuccess,
  onPrompted,
  onDismissed,
  onSkipped,
  trackEvent,
  trackError,
}: PromptGoogleOneTapOptions<AppCategory, AppActions>): Promise<void> {
  const google = await loadGoogleOneTap();
  if (google) {
    google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => {
        // @ts-expect-error - Incorrect EventTrackingActions typing with category overrides
        trackEvent('google-one-tap-success', analyticsName, undefined, {
          categoryOverride: 'login',
        });
        onSuccess(response);
      },
      use_fedcm_for_prompt: true,
    });
    google.accounts.id.prompt((n) => {
      const momentType = n.getMomentType();
      if (momentType === 'display') {
        // @ts-expect-error - Incorrect EventTrackingActions typing with category overrides
        trackEvent('google-one-tap-displayed', analyticsName, undefined, {
          categoryOverride: 'login',
        });
        onPrompted && onPrompted(true, undefined);
      } else if (n.isDismissedMoment()) {
        trackEvent(
          // @ts-expect-error - Incorrect EventTrackingActions typing with category overrides
          'google-one-tap-dismissed',
          analyticsName,
          { reason: n.getDismissedReason() },
          { categoryOverride: 'login' },
        );
        onDismissed && onDismissed(n.getDismissedReason());
      } else if (n.isSkippedMoment()) {
        // @ts-expect-error - Incorrect EventTrackingActions typing with category overrides
        trackEvent('google-one-tap-skipped', analyticsName, undefined, {
          categoryOverride: 'login',
        });
        onSkipped && onSkipped();
      } else {
        // google one-tap could not be displayed for multiple reasons now that FedCM is enabled (users can disable one-tap from the browser settings, etc.)
        // we can not get the exact reason due to privacy restrictions but we can still track when this is not displayed
        trackError(
          'google-one-tap',
          new Error('google one tap not displayed'),
          { reason: 'unknown' },
          { skipSentry: true },
        );
        trackEvent(
          // @ts-expect-error - Incorrect EventTrackingActions typing with category overrides
          'google-one-tap-not-displayed',
          analyticsName,
          { reason: 'unknown' },
          { categoryOverride: 'login' },
        );
        onPrompted && onPrompted(false, undefined);
      }
    });
  } else {
    const reason = 'script_load_failed';
    trackError(
      'google-one-tap',
      new Error('google one tap not displayed'),
      { reason },
      { skipSentry: true },
    );
    trackEvent(
      // @ts-expect-error - Incorrect EventTrackingActions typing with category overrides
      'google-one-tap-not-displayed',
      analyticsName,
      { reason },
      { categoryOverride: 'login' },
    );
    onPrompted && onPrompted(false, 'script_load_failed');
  }
}
